import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import {
	ProductCreatePayload,
	ProductUpdatePayload,
	Product,
} from '@mira-avant/mtlibs';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class ProductService {
	private readonly BASE_PATH = 'products';

	public selectedProductBs$ = new BehaviorSubject<Product | undefined>(undefined);
	public readonly selectedProduct$ = this.selectedProductBs$.asObservable();

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public productsBs$: BehaviorSubject<Product[]> = new BehaviorSubject<Product[]>([]);
	public products$: Observable<Product[]> = this.productsBs$.pipe(
		filter(products => products !== undefined && products.length > 0)
	);

	constructor(private api: ApiService) {
		// Load products on init
		this.getProducts$().catch(error => {
			console.error('Error fetching products:', error);
			this.productsBs$.next([]);
		});
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public selectProduct(product: Product | undefined): void {
		this.selectedProductBs$.next(product);
	}

	public async getProducts$(): Promise<Product[]> {
		return this.withLoading(async () => {
			const products = await firstValueFrom(this.api.get<Product[]>(this.BASE_PATH + '/all'));
			this.productsBs$.next(products);
			return products;
		});
	}

	public async getProductByName$(name: string): Promise<Product> {
		return this.withLoading(async () => {
			const products = await firstValueFrom(this.api.get<Product>(this.BASE_PATH + '/byName/' + name));
			return products;
		});
	}

	public async deleteProduct$(name: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete(`${this.BASE_PATH}/${name}`));
			this.productsBs$.next(this.productsBs$.value.filter(r => r.name !== name));
			if (this.selectedProductBs$.value?.name === name) {
				this.selectProduct(undefined);
			}
		});
	}

	public async createProduct$(payload: ProductCreatePayload): Promise<Product> {
		return this.withLoading(async () => {
			const product = await firstValueFrom(this.api.post<Product>(this.BASE_PATH, payload));
			this.productsBs$.next([...this.productsBs$.value, product]);
			return product;
		});
	}

	public async updateProduct$(code: string, payload: ProductUpdatePayload): Promise<Product> {
		return this.withLoading(async () => {
			const updatedProduct = await firstValueFrom(
				this.api.put<Product>(`${this.BASE_PATH}/${code}`, payload)
			);
			const currentProducts = this.productsBs$.value;
			const index = currentProducts.findIndex(r => r.name === code);
			if (index !== -1) {
				currentProducts[index] = updatedProduct;
				this.productsBs$.next([...currentProducts]);
			}
			return updatedProduct;
		});
	}
}
